import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Spin,
  Button,
  Space,
  Typography,
  Tooltip,
  message,
  Image,
  Avatar,
  Tag,
  Result,
} from "antd";
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  HeartOutlined,
  HeartFilled,
  FilePdfOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  LockOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { resourceDetails } from "services/actions/resourceActoin";
import { useDispatch, useSelector } from "react-redux";

const { Title, Text } = Typography;

const THEME = {
  primary: "#5356FB",
  primaryHover: "#4245E0",
  primaryLight: "#EEF0FE",
  bgSubtle: "#F5F7FB",
  textDark: "#1E293B",
  textMuted: "#64748B",
  border: "#E2E8F0",
  likeRed: "#EF4444",
  likeRedLight: "#FEE2E2",
};

interface ResourceViewerProps {
  url?: string;
  title?: string;
}

const ResourceViewer: React.FC<ResourceViewerProps> = ({
  url: directPropUrl,
  title: directPropTitle,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id, token, guid } = useParams<{ id?: string; token?: string; guid?: string }>();

  // Dynamic States
  const [resolvedUrl, setResolvedUrl] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [fileType, setFileType] = useState<"PDF" | "Image" | "Video" | "Document">("PDF");
  const [videoEmbedUrl, setVideoEmbedUrl] = useState<string>("");
  const [isExternalVideo, setIsExternalVideo] = useState<boolean>(false);

  const [mediaLoading, setMediaLoading] = useState<boolean>(true);
  const [errorStatus, setErrorStatus] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [reloadKey, setReloadKey] = useState<number>(0);

  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [likeCount, setLikeCount] = useState<number>(1);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
const [pdfBlobUrl, setPdfBlobUrl] = useState<string>("");

  const { resourceDetailsLoading, resourceDetailsInfo, resourceDetailsError } = useSelector(
    (state: any) => state.resource || {}
  );

  const formatMediaUrl = (rawUrl: string) => {
    const url = rawUrl.trim();

    if (url.includes("drive.google.com")) {
      const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (match && match[1]) {
        return {
          embedUrl: `https://drive.google.com/file/d/${match[1]}/preview`,
          isEmbed: true,
          platform: "Google Drive",
        };
      }
    }

    if (url.includes("youtu.be/")) {
      const videoId = url.split("youtu.be/")[1]?.split("?")[0];
      return {
        embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
        isEmbed: true,
        platform: "YouTube",
      };
    }

    if (url.includes("youtube.com/watch")) {
      const match = url.match(/[?&]v=([^&]+)/);
      const videoId = match ? match[1] : "";
      return {
        embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
        isEmbed: true,
        platform: "YouTube",
      };
    }

    if (url.includes("youtube.com/shorts/")) {
      const videoId = url.split("youtube.com/shorts/")[1]?.split("?")[0];
      return {
        embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1`,
        isEmbed: true,
        platform: "YouTube",
      };
    }

    if (url.includes("vimeo.com/")) {
      const match = url.match(/vimeo\.com\/(\d+)/);
      const videoId = match ? match[1] : "";
      return {
        embedUrl: `https://player.vimeo.com/video/${videoId}?autoplay=1`,
        isEmbed: true,
        platform: "Vimeo",
      };
    }

    if (url.includes("dropbox.com")) {
      const cleanUrl = url.replace("dl=0", "raw=1");
      return { embedUrl: cleanUrl, isEmbed: false, platform: "Dropbox" };
    }

    if (url.includes("onedrive.live.com") || url.includes("1drv.ms")) {
      const embedUrl = url.replace("/view.aspx", "/embed.aspx");
      return { embedUrl, isEmbed: true, platform: "OneDrive" };
    }

    return { embedUrl: url, isEmbed: false, platform: "Direct Asset" };
  };

  useEffect(() => {
  let isMounted = true;
  let objectUrl = "";

  if (resolvedUrl && (fileType === "PDF" || fileType === "Document")) {
    setMediaLoading(true);

    if (isOfficeDoc(resolvedUrl)) {
      setPdfBlobUrl(resolvedUrl);
      return;
    }

    fetch(resolvedUrl)
      .then((res) => {
        if (!res.ok) throw new Error("PDF download failed");
        return res.blob();
      })
      .then((blob) => {
        if (isMounted) {
          objectUrl = URL.createObjectURL(blob);
          setPdfBlobUrl(objectUrl);
          setTimeout(() => {
            if (isMounted) setMediaLoading(false);
          }, 500);
        }
      })
      .catch((err) => {
        console.error("PDF load error:", err);
        if (isMounted) {
          setPdfBlobUrl(resolvedUrl); 
          setMediaLoading(false);
        }
      });
  } else {
    setPdfBlobUrl("");
  }

  return () => {
    isMounted = false;
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  };
}, [resolvedUrl, reloadKey, fileType]);

  const detectFileType = (
    urlStr: string = "",
    mimeType: string = "",
    resourceType: string = ""
  ): "PDF" | "Image" | "Video" | "Document" => {
    const lowerType = resourceType.toLowerCase();
    const lowerMime = mimeType.toLowerCase();
    const cleanUrl = urlStr.split("?")[0].toLowerCase();

    if (
      lowerType === "video" ||
      lowerMime.startsWith("video/") ||
      urlStr.includes("youtube.com") ||
      urlStr.includes("youtu.be") ||
      urlStr.includes("vimeo.com") ||
      /\.(mp4|webm|ogg|mov|mkv)$/i.test(cleanUrl)
    ) {
      return "Video";
    }

    if (
      lowerType === "image" ||
      lowerMime.startsWith("image/") ||
      /\.(png|jpe?g|webp|gif|svg|bmp)$/i.test(cleanUrl)
    ) {
      return "Image";
    }

    if (lowerMime.includes("pdf") || /\.(pdf)$/i.test(cleanUrl)) {
      return "PDF";
    }

    return "Document";
  };

  useEffect(() => {
    const rawTargetGuid = id || token || guid;
    if (rawTargetGuid) {
      const cleanGuid = rawTargetGuid.replace(/-/g, "");
      dispatch(resourceDetails({ guid: cleanGuid }) as any);
    }
  }, [id, token, guid, reloadKey, dispatch]);

  useEffect(() => {
    if (resourceDetailsError) {
      const errorMsg =
        typeof resourceDetailsError === "string"
          ? resourceDetailsError
          : resourceDetailsError?.detail ||
            resourceDetailsError?.message ||
            "Failed to load resource details.";
      setErrorStatus(errorMsg);
      setMediaLoading(false);
    }
  }, [resourceDetailsError]);

 useEffect(() => {
  const apiData = resourceDetailsInfo?.data || resourceDetailsInfo;
  const queryParams = new URLSearchParams(location.search);
  const rawUrlParam = queryParams.get("url") || queryParams.get("f") || queryParams.get("path");

  if (apiData && (apiData.file_path || apiData.resource_url)) {
    const targetPath = apiData.file_path || apiData.resource_url || "";
    const detected = detectFileType(targetPath, apiData.mime_type || "", apiData.resource_type || "");

    setMediaLoading(true); 
    setResolvedUrl(targetPath);
    setFileType(detected);
    setFileName(apiData.file_name || apiData.title || apiData.original_file_name || "Resource Asset");

    if (detected === "Video") {
      const { embedUrl, isEmbed } = formatMediaUrl(targetPath);
      setVideoEmbedUrl(embedUrl);
      setIsExternalVideo(isEmbed);
    }
    setErrorStatus(null);
  } else if (directPropUrl || rawUrlParam) {
    const fallbackUrl = directPropUrl || (rawUrlParam ? decodeURIComponent(rawUrlParam) : "");
    const detected = detectFileType(fallbackUrl);

    setMediaLoading(true); 
    setResolvedUrl(fallbackUrl);
    setFileType(detected);
    setFileName(directPropTitle || "Resource Asset");

    if (detected === "Video") {
      const { embedUrl, isEmbed } = formatMediaUrl(fallbackUrl);
      setVideoEmbedUrl(embedUrl);
      setIsExternalVideo(isEmbed);
    }
    setErrorStatus(null);
  } else if (!resourceDetailsLoading && !id && !token && !guid) {
    setErrorStatus("Resource identifier or file path not found.");
  }
}, [resourceDetailsInfo, directPropUrl, directPropTitle, location.search, id, token, guid, resourceDetailsLoading]);

  const handleLikeToggle = () => {
    if (isLiked) {
      setIsLiked(false);
      setLikeCount((prev) => prev - 1);
      message.info("Removed from favorites");
    } else {
      setIsLiked(true);
      setLikeCount((prev) => prev + 1);
      message.success("Added to favorites!");
    }
  };

    const isOfficeDoc = (url: string = "") => {
    const cleanUrl = url.split("?")[0].toLowerCase();
    return /\.(docx?|xlsx?|pptx?)$/i.test(cleanUrl);
    };

  const handleDownload = async () => {
    if (!resolvedUrl) return;
  
    if (isExternalVideo) {
      window.open(resolvedUrl, "_blank");
      message.info("Redirecting to source host...");
      return;
    }
  
    setDownloadLoading(true); 
  
    try {
      const response = await fetch(resolvedUrl);
      if (!response.ok) throw new Error("Fetch failed");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const downloadAnchor = document.createElement("a");
      downloadAnchor.style.display = "none";
      downloadAnchor.href = blobUrl;
      downloadAnchor.download = fileName || "downloaded-resource";
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      document.body.removeChild(downloadAnchor);
      window.URL.revokeObjectURL(blobUrl);
      message.success({content: "Download completed successfully!",key: "downloading",});
    } catch {
      const downloadAnchor = document.createElement("a");
      downloadAnchor.href = resolvedUrl;
      downloadAnchor.setAttribute("download", fileName || "downloaded-resource");
      downloadAnchor.setAttribute("target", "_blank");
      downloadAnchor.setAttribute("rel", "noopener noreferrer");
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
      message.destroy("downloading");
    } finally {
      setDownloadLoading(false); 
    }
  };

  if (errorStatus && !resourceDetailsLoading) {
    return (
      <div style={{ padding: "60px 16px", backgroundColor: THEME.bgSubtle, minHeight: "100vh" }}>
        <Result
          status={errorStatus.toLowerCase().includes("invalid") ? "403" : "404"}
          icon={<LockOutlined style={{ color: "#ef4444" }} />}
          title="Access Restricted"
          subTitle={errorStatus}
          extra={
            <Button
              type="primary"
              style={{ backgroundColor: THEME.primary, borderColor: THEME.primary }}
              onClick={() => navigate("/login")}
            >
              Go to Login
            </Button>
          }
        />
      </div>
    );
  }

  useEffect(() => {
  if (resolvedUrl && (fileType === "PDF" || fileType === "Document")) {
    setMediaLoading(true);
  }
}, [resolvedUrl, reloadKey, fileType]);

  return (
    <div
      style={{
        backgroundColor: THEME.bgSubtle,
        minHeight: "100vh",
        padding: isFullscreen ? 0 : "12px 12px 16px 12px",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {!isFullscreen && (
        <div
          style={{
            marginBottom: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Space size={8}>
            <Button
              shape="circle"
              size="middle"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              style={{ borderColor: THEME.border }}
            />
            <div>
              <Title
                level={4}
                style={{
                  margin: 0,
                  color: THEME.textDark,
                  fontWeight: 700,
                  fontSize: "clamp(15px, 2.5vw, 18px)",
                }}
              >
                Resource Viewer
              </Title>
              <Text type="secondary" style={{ fontSize: "clamp(10px, 1.8vw, 12px)" }}>
                EEG Data Hub / Asset View
              </Text>
            </div>
          </Space>
        </div>
      )}

      <Card
        bordered={false}
        style={{
          borderRadius: isFullscreen ? 0 : 12,
          height: isFullscreen ? "100vh" : "calc(100vh - 84px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
          border: isFullscreen ? "none" : `1px solid ${THEME.border}`,
        }}
        styles={{
          body: {
            padding: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          },
        }}
      >
        <div
          style={{
            padding: "10px 16px",
            backgroundColor: "#FFFFFF",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            borderBottom: `1px solid ${THEME.border}`,
            flexShrink: 0,
          }}
        >
          <Space size={10} align="center" style={{ minWidth: 0, flex: "1 1 auto" }}>
            <Avatar
              shape="square"
              size={36}
              style={{
                backgroundColor: THEME.primaryLight,
                color: THEME.primary,
                borderRadius: 8,
                fontSize: 16,
                flexShrink: 0,
              }}
              icon={
                fileType === "PDF" ? (
                  <FilePdfOutlined />
                ) : fileType === "Image" ? (
                  <FileImageOutlined />
                ) : fileType === "Video" ? (
                  <VideoCameraOutlined />
                ) : (
                  <FileTextOutlined />
                )
              }
            />
            <div style={{ minWidth: 0 }}>
              <Title
                level={5}
                ellipsis
                style={{
                  margin: 0,
                  color: THEME.textDark,
                  fontWeight: 700,
                  fontSize: 14,
                  maxWidth: "clamp(160px, 40vw, 420px)",
                }}
              >
                {fileName || "Resource Asset"}
              </Title>
              <Space size={6} wrap>
                <Tag
                  style={{
                    borderRadius: 10,
                    fontSize: 10,
                    marginRight: 0,
                    lineHeight: "18px",
                    backgroundColor:
                      fileType === "PDF"
                        ? "#fee2e2"
                        : fileType === "Image"
                        ? "#dcfce7"
                        : "#ede9fe",
                    color:
                      fileType === "PDF"
                        ? "#dc2626"
                        : fileType === "Image"
                        ? "#16a34a"
                        : "#7c3aed",
                    border: "none",
                    fontWeight: 600,
                  }}
                >
                  {fileType}
                </Tag>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {isExternalVideo ? "Stream Asset" : "Direct Storage"}
                </Text>
              </Space>
            </div>
          </Space>

          <Space size={6} wrap style={{ justifyContent: "flex-end", flexShrink: 0 }}>
            {/* <Tooltip title={isLiked ? "Unlike" : "Like"}>
              <Button
                size="small"
                onClick={handleLikeToggle}
                icon={
                  isLiked ? (
                    <HeartFilled style={{ color: THEME.likeRed }} />
                  ) : (
                    <HeartOutlined style={{ color: THEME.textMuted }} />
                  )
                }
                style={{
                  borderRadius: 6,
                  borderColor: isLiked ? THEME.likeRed : THEME.border,
                  backgroundColor: isLiked ? THEME.likeRedLight : "#FFFFFF",
                  color: isLiked ? THEME.likeRed : THEME.textDark,
                  fontWeight: 600,
                  height: 32,
                  padding: "0 10px",
                }}
              >
                {likeCount}
              </Button>
            </Tooltip> */}

           <Tooltip title={downloadLoading ? "Downloading..." : "Download File"}>
              <Button
                size="small"
                style={{ height: 32 }}
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                loading={downloadLoading} 
                disabled={mediaLoading || resourceDetailsLoading || downloadLoading}
              />
            </Tooltip>

            {/* <Tooltip title="Reload">
              <Button
                size="small"
                style={{ height: 32 }}
                icon={<ReloadOutlined />}
                onClick={() => {
                  setMediaLoading(true);
                  setReloadKey((k) => k + 1);
                }}
              />
            </Tooltip> */}

            <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
              <Button
                size="small"
                type="primary"
                style={{
                  backgroundColor: THEME.primary,
                  borderColor: THEME.primary,
                  height: 32,
                }}
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => setIsFullscreen((prev) => !prev)}
              />
            </Tooltip>
          </Space>
        </div>

        <div
          style={{
            flex: 1,
            position: "relative",
            backgroundColor: "#0F172A",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            width: "100%",
            height: "100%",
          }}
        >
      {(mediaLoading || resourceDetailsLoading) && (
  <div
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(15, 23, 42, 0.85)", 
      zIndex: 100, 
      backdropFilter: "blur(4px)",
    }}
  >
    <Spin size="large" />
    <Text style={{ marginTop: 14, color: "#FFFFFF", fontSize: 14, fontWeight: 500 }}>
      Loading document...
    </Text>
  </div>
)}

          {fileType === "Image" && resolvedUrl && (
            <div
              key={reloadKey}
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "clamp(8px, 2vw, 24px)",
                backgroundColor: "#F8FAFC",
                overflow: "auto",
              }}
            >
              <Image
                src={resolvedUrl}
                alt="preview"
                onLoad={() => setMediaLoading(false)}
                onError={() => {
                  setMediaLoading(false);
                  message.error("Failed to load image from storage.");
                }}
                style={{
                  maxHeight: isFullscreen ? "94vh" : "calc(100vh - 140px)",
                  maxWidth: "100%",
                  objectFit: "contain",
                  borderRadius: 8,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                }}
              />
            </div>
          )}

{(fileType === "PDF" || fileType === "Document") && (
  <>
    {pdfBlobUrl && (
      <iframe
        key={`${pdfBlobUrl}-${reloadKey}`}
        src={
          isOfficeDoc(resolvedUrl)
            ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(resolvedUrl)}`
            : `${pdfBlobUrl}#toolbar=0`
        }
        title="Document Viewer"
        onLoad={() => {
          if (isOfficeDoc(resolvedUrl)) {
            setMediaLoading(false);
          }
        }}
        onError={() => {
          setMediaLoading(false);
          message.error("Unable to embed document.");
        }}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          backgroundColor: "#FFFFFF",
          visibility: mediaLoading ? "hidden" : "visible",
        }}
      />
    )}
  </>
)}
          {fileType === "Video" && (
            isExternalVideo ? (
              <iframe
                key={reloadKey}
                src={videoEmbedUrl}
                title="Cloud Stream Player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onLoad={() => setMediaLoading(false)}
                onError={() => {
                  setMediaLoading(false);
                  message.error("Unable to stream video from host.");
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  backgroundColor: "#000",
                }}
              />
            ) : (
              <video
                key={reloadKey}
                src={resolvedUrl}
                controls
                autoPlay
                playsInline
                onLoadedData={() => setMediaLoading(false)}
                onError={() => {
                  setMediaLoading(false);
                  message.error("Failed to stream video.");
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  maxHeight: isFullscreen ? "100vh" : "calc(100vh - 140px)",
                  outline: "none",
                  backgroundColor: "#0F172A",
                  objectFit: "contain",
                }}
              />
            )
          )}
        </div>
      </Card>
    </div>
  );
};

export default ResourceViewer;